import Stack from "@mui/material/Stack";
import { FileComponent } from "@/widgets/file-uploader-widget/file-component";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import {
  FileTypeENUM,
  IUploadingFileGroup,
} from "@/widgets/file-uploader-widget/interface";
import { useCallback, useRef, useState } from "react";
import { Divider, IconButton } from "@mui/material";
import { useUploadFiles } from "@/widgets/production-floor/views/board-missions-view/navigation-buttons/use-upload-files";
import { FONT_FAMILY } from "@/utils/font-family";
import FolderIcon from "@mui/icons-material/Folder";
import { useRouter } from "next/router";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { OpenUploadFileIcon } from "./icons";
import { GoMakeDeleteModal } from "@/components";
import { useModal } from "@/hooks/use-modal";
import { ModalType } from "@/enums/tabs-icon-enums";
import { useGomakeAxios } from "@/hooks";
import { printJobUsingPrintNodeApi } from "@/services/api-service/board-missions/board-missions-table";
import { selectedMissionsState } from "../production-floor/state/boards";
import { useRecoilState, useRecoilValue } from "recoil";
import { CopyIcon } from "@/icons/shared/copy-icon";
import { boardMissionModalState } from "../production-floor/state/board-mission-modal";
import { printHouseProfile } from "@/store/print-house-profile";
import { isMiscellaneousFilesEnabled } from "@/widgets/file-uploader-widget/file-api-utils";
import { fileUploaderTargetSkuState } from "@/store/file-uploader-state";
import { productWorksTypesState } from "@/widgets/production-floor/views/board-missions-view/stations/state";
import { getBoardsMissionsStations } from "@/services/api-service/production-floor/production-floor-endpoints";
import {
  AssignFilesToTypesDialog,
  IAssignFileTypeOption,
  IFileTypeAssignment,
} from "@/widgets/file-uploader-widget/assign-files-to-types-dialog";

interface IPendingAssignUpload {
  files: File[];
  fileType: FileTypeENUM;
  types: IAssignFileTypeOption[];
}

const FilesGroupComponent = ({
  title,
  filesInfo,
  orderItemId,
  filePath,
  graphicsFilePath,
  miscellaneousFilePath,
  boardMissionId,
  isComplexProduct,
  productType,
  onFileDeleted
}: IUploadingFileGroup) => {
  const { primaryColor } = useGomakeTheme();
  const [isOverGraphics, setIsOverGraphics] = useState(false);
  const [isOverPrinting, setIsOverPrinting] = useState(false);
  const [isOverMiscellaneous, setIsOverMiscellaneous] = useState(false);
  const [pendingAssign, setPendingAssign] = useState<IPendingAssignUpload | null>(
    null
  );
  const [isAssignUploading, setIsAssignUploading] = useState(false);
  const isOpenPrintingModal = useModal();
  const { callApi } = useGomakeAxios();

  const { handleFileUpload, onClickCopyPath, finalFilePath } = useUploadFiles(
    orderItemId,
    filePath,
    graphicsFilePath,
    isComplexProduct,
    productType,
    isOpenPrintingModal,
    miscellaneousFilePath,
  );
  const { push, pathname } = useRouter();
  const fileInputRef = useRef<HTMLInputElement & { fileType?: FileTypeENUM }>(null);
  const { t } = useTranslation();
  const [selectedMission, setSelectedMission] = useRecoilState<any>(
    selectedMissionsState
  );
  const [boardMissionModal, setBoardMissionModal] = useRecoilState(
    boardMissionModalState
  );
  const [companyProfile] = useRecoilState(printHouseProfile);
  const targetSku = useRecoilValue(fileUploaderTargetSkuState);
  const productWorksTypes = useRecoilValue(productWorksTypesState);
  const showMiscellaneousFiles = isMiscellaneousFilesEnabled(
    companyProfile?.filesApiAddress,
  );
  const columnWidth = showMiscellaneousFiles ? "33.33%" : "50%";

  const resolveWorkTypes = useCallback(async (): Promise<IAssignFileTypeOption[]> => {
    const openJobMatches =
      !!boardMissionId &&
      boardMissionModal?.boardMissionsId === boardMissionId &&
      Array.isArray(productWorksTypes) &&
      productWorksTypes.length > 0;

    if (openJobMatches) {
      return (productWorksTypes as IAssignFileTypeOption[])
        .map((type) => ({ name: String(type?.name || "").trim() }))
        .filter((type) => !!type.name);
    }

    if (!boardMissionId) {
      return [];
    }

    return new Promise((resolve) => {
      getBoardsMissionsStations(
        callApi,
        (res) => {
          const workTypes = res?.data?.workTypes;
          if (!res?.success || !Array.isArray(workTypes)) {
            resolve([]);
            return;
          }
          resolve(
            workTypes
              .map((type: { name?: string }) => ({
                name: String(type?.name || "").trim(),
              }))
              .filter((type: IAssignFileTypeOption) => !!type.name)
          );
        },
        {
          boardMissionsId: boardMissionId,
          productType: productType || "",
        }
      );
    });
  }, [
    boardMissionId,
    boardMissionModal?.boardMissionsId,
    callApi,
    productType,
    productWorksTypes,
  ]);

  const uploadFilesWithAssignments = useCallback(
    async (assignments: IFileTypeAssignment[], fileType: FileTypeENUM) => {
      const uploadPromises = assignments.map((assignment) =>
        handleFileUpload(assignment.file, fileType, assignment.skus)
      );
      await Promise.all(uploadPromises);
    },
    [handleFileUpload]
  );

  const onFilesChosen = useCallback(
    async (fileList: FileList | File[], fileType: FileTypeENUM) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) {
        return;
      }

      // Existing Types → Link → Upload path already sets a target SKU.
      if (targetSku?.trim()) {
        await Promise.all(
          files.map((file) => handleFileUpload(file, fileType))
        );
        return;
      }

      const types = await resolveWorkTypes();
      if (types.length > 1) {
        setPendingAssign({ files, fileType, types });
        return;
      }

      await Promise.all(files.map((file) => handleFileUpload(file, fileType)));
    },
    [handleFileUpload, resolveWorkTypes, targetSku]
  );

  const handleAssignConfirm = async (assignments: IFileTypeAssignment[]) => {
    if (!pendingAssign || isAssignUploading) {
      return;
    }
    const fileType = pendingAssign.fileType;
    setIsAssignUploading(true);
    try {
      await uploadFilesWithAssignments(assignments, fileType);
      setPendingAssign(null);
    } finally {
      setIsAssignUploading(false);
    }
  };

  const handleAssignCancel = async () => {
    if (!pendingAssign || isAssignUploading) {
      return;
    }
    const { files, fileType } = pendingAssign;
    setIsAssignUploading(true);
    try {
      await Promise.all(
        files.map((file) => handleFileUpload(file, fileType, []))
      );
      setPendingAssign(null);
    } finally {
      setIsAssignUploading(false);
    }
  };

  const handleGraphicsDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsOverGraphics(false);
    await onFilesChosen(event.dataTransfer.files, FileTypeENUM.Graphics);
  };

  const handlePrintingDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsOverPrinting(false);
    await onFilesChosen(event.dataTransfer.files, FileTypeENUM.Printing);
  };

  const handleMiscellaneousDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsOverMiscellaneous(false);
    await onFilesChosen(event.dataTransfer.files, FileTypeENUM.Miscellaneous);
  };

  const handleGraphicsDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsOverGraphics(true);
  }, []);

  const handlePrintingDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsOverPrinting(true);
  }, []);

  const handleGraphicsDragLeave = useCallback(() => {
    setIsOverGraphics(false);
  }, []);

  const handlePrintingDragLeave = useCallback(() => {
    setIsOverPrinting(false);
  }, []);

  const handleMiscellaneousDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsOverMiscellaneous(true);
  }, []);

  const handleMiscellaneousDragLeave = useCallback(() => {
    setIsOverMiscellaneous(false);
  }, []);

  const getUniqueFilesByType = (fileType: FileTypeENUM) =>
    filesInfo
      .filter((file) => file.fileType === fileType)
      .reduce((unique, item) => {
        const exists = unique.find(
          (file) =>
            file.fileName === item.fileName &&
            (item.uploadId ? file.uploadId === item.uploadId : true),
        );
        if (!exists) {
          unique.push(item);
        } else if (
          exists &&
          item.uploadId &&
          exists.uploadId !== item.uploadId
        ) {
          const index = unique.findIndex((f) => f === exists);
          unique[index] = item;
        }
        return unique;
      }, [] as typeof filesInfo);

  const graphicsFiles = getUniqueFilesByType(FileTypeENUM.Graphics);
  const printingFiles = getUniqueFilesByType(FileTypeENUM.Printing);
  const miscellaneousFiles = getUniqueFilesByType(FileTypeENUM.Miscellaneous);

  const handelChooseFileClick = (fileType: FileTypeENUM) => {
    if (fileInputRef.current) {
      fileInputRef.current.fileType = fileType;
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileType = (e.target as HTMLInputElement & { fileType?: FileTypeENUM }).fileType;
    const files = e.target.files;
    if (files && fileType !== undefined) {
      await onFilesChosen(files, fileType);
    }
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const printJobUsingPrintNode = async (jobId: string, productType?: string) => {
    const callBack = (res) => {
      if (res.success) {
        // Handle success
      }
    };
    let data = jobId;
    if (productType) {
      // data = data + "?productType=" + productType;
    }
    await printJobUsingPrintNodeApi(callApi, callBack, data);
  };

  return (
    <Stack
      minHeight={"50px"}
      position={"relative"}
      width={showMiscellaneousFiles ? "720px" : "500px"}
    >
      <Stack
        direction={"row"}
        gap={"10px"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <h3 style={{ color: primaryColor(400), ...FONT_FAMILY.Inter(700, 14) }}>
          {title}
        </h3>
        {pathname !== "/production-floor" && (
          <Stack direction={"row"} alignItems={"center"}>
            {finalFilePath && <IconButton
              style={{
                width: "25px",
                height: "25px",
                padding: "2px",
              }}
              onClick={() => onClickCopyPath()}
            >

              <CopyIcon width={20} height={20} />
            </IconButton>}
            <IconButton
              style={{
                width: "25px",
                height: "25px",
                padding: "2px",
              }}
              onClick={() => {
                setBoardMissionModal({
                  isOpen: true,
                  boardMissionsId: boardMissionId || null,
                  productType: productType || "",
                  step: "stations",
                });
                push("/production-floor").then();
              }}
            >
              <FolderIcon />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        gap="20px"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        {/* Graphics Files Section */}
        <Stack
          gap="10px"
          onDrop={handleGraphicsDrop}
          onDragOver={handleGraphicsDragOver}
          onDragLeave={handleGraphicsDragLeave}
          className={`drop-zone ${isOverGraphics ? "dragging" : ""}`}
          width={columnWidth}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <h3 style={{ ...FONT_FAMILY.Inter(600, 14) }}>
              {t("fileUploader.originalFiles")}
            </h3>
            {graphicsFiles?.length > 0 && (
              <IconButton
                style={{
                  width: "25px",
                  height: "25px",
                  padding: "2px",
                }}
                onClick={() => handelChooseFileClick(FileTypeENUM.Graphics)}
              >
                <OpenUploadFileIcon />
              </IconButton>
            )}
          </Stack>
          <Stack gap="10px">
            {graphicsFiles?.length === 0 && (
              <Stack direction={"row"} alignItems={"center"}>
                <Button
                  onClick={() => handelChooseFileClick(FileTypeENUM.Graphics)}
                  style={{
                    color: primaryColor(700),
                    ...FONT_FAMILY.Lexend(700, 15),
                    textTransform: "none",
                    gap: "5px",
                  }}
                  startIcon={<OpenUploadFileIcon />}
                >
                  {t("fileUploader.chooseFile")}
                </Button>
                <span
                  style={{
                    color: primaryColor(700),
                    ...FONT_FAMILY.Inter(500, 15),
                  }}
                >
                  {t("fileUploader.dropFile")}
                </span>
              </Stack>
            )}
            <Stack>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}>
                {graphicsFiles.map((file, index) => (
                  <FileComponent
                    key={`${file.fileName}-${file.uploadId || index}`}
                    {...file}
                    filePath={graphicsFilePath || filePath}
                    bucketKey={file.bucketKey}
                    orderItemId={orderItemId}
                    productType={productType}
                    onFileDeleted={onFileDeleted}
                  />
                ))}
              </div>
            </Stack>
          </Stack>
        </Stack>
        {/* Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            borderColor: "#ECECEC",
          }}
        />
        {/* Printing Files Section */}
        <Stack
          gap="10px"
          onDrop={handlePrintingDrop}
          onDragOver={handlePrintingDragOver}
          onDragLeave={handlePrintingDragLeave}
          className={`drop-zone ${isOverPrinting ? "dragging" : ""}`}
          width={columnWidth}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <h3 style={{ ...FONT_FAMILY.Inter(600, 14) }}>
              {t("fileUploader.printingFiles")}
            </h3>
            {printingFiles?.length > 0 && (
              <IconButton
                style={{
                  width: "25px",
                  height: "25px",
                  padding: "2px",
                }}
                onClick={() => handelChooseFileClick(FileTypeENUM.Printing)}
              >
                <OpenUploadFileIcon />
              </IconButton>
            )}
          </Stack>
          <Stack gap="10px">
            {printingFiles?.length === 0 && (
              <Stack direction={"row"} alignItems={"center"}>
                <Button
                  onClick={() => handelChooseFileClick(FileTypeENUM.Printing)}
                  style={{
                    color: primaryColor(700),
                    ...FONT_FAMILY.Lexend(700, 15),
                    textTransform: "none",
                    gap: "5px",
                  }}
                  startIcon={<OpenUploadFileIcon />}
                >
                  {t("fileUploader.chooseFile")}
                </Button>

                <span
                  style={{
                    color: primaryColor(700),
                    ...FONT_FAMILY.Inter(500, 15),
                  }}
                >
                  {t("fileUploader.dropFile")}
                </span>
              </Stack>
            )}
            <Stack>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}>
                {printingFiles.map((file, index) => (
                  <FileComponent
                    key={`${file.fileName}-${file.uploadId || index}`}
                    {...file}
                    filePath={filePath}
                    bucketKey={file.bucketKey}
                    orderItemId={orderItemId}
                    productType={productType}
                    onFileDeleted={onFileDeleted}
                  />
                ))}
              </div>
            </Stack>
          </Stack>
        </Stack>
        {showMiscellaneousFiles && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: "#ECECEC",
              }}
            />
            {/* Miscellaneous Files Section */}
            <Stack
              gap="10px"
              onDrop={handleMiscellaneousDrop}
              onDragOver={handleMiscellaneousDragOver}
              onDragLeave={handleMiscellaneousDragLeave}
              className={`drop-zone ${isOverMiscellaneous ? "dragging" : ""}`}
              width={columnWidth}
            >
              <Stack direction={"row"} alignItems={"center"}>
                <h3 style={{ ...FONT_FAMILY.Inter(600, 14) }}>
                  {t("fileUploader.miscellaneousFiles")}
                </h3>
                {miscellaneousFiles?.length > 0 && (
                  <IconButton
                    style={{
                      width: "25px",
                      height: "25px",
                      padding: "2px",
                    }}
                    onClick={() =>
                      handelChooseFileClick(FileTypeENUM.Miscellaneous)
                    }
                  >
                    <OpenUploadFileIcon />
                  </IconButton>
                )}
              </Stack>
              <Stack gap="10px">
                {miscellaneousFiles?.length === 0 && (
                  <Stack direction={"row"} alignItems={"center"}>
                    <Button
                      onClick={() =>
                        handelChooseFileClick(FileTypeENUM.Miscellaneous)
                      }
                      style={{
                        color: primaryColor(700),
                        ...FONT_FAMILY.Lexend(700, 15),
                        textTransform: "none",
                        gap: "5px",
                      }}
                      startIcon={<OpenUploadFileIcon />}
                    >
                      {t("fileUploader.chooseFile")}
                    </Button>
                    <span
                      style={{
                        color: primaryColor(700),
                        ...FONT_FAMILY.Inter(500, 15),
                      }}
                    >
                      {t("fileUploader.dropFile")}
                    </span>
                  </Stack>
                )}
                <Stack>
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    {miscellaneousFiles.map((file, index) => (
                      <FileComponent
                        key={`${file.fileName}-${file.uploadId || index}`}
                        {...file}
                        filePath={miscellaneousFilePath || filePath}
                        bucketKey={file.bucketKey}
                        orderItemId={orderItemId}
                        productType={productType}
                        onFileDeleted={onFileDeleted}
                      />
                    ))}
                  </div>
                </Stack>
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
      <input
        type={"file"}
        multiple
        value={""}
        onChange={handleFileSelect}
        hidden={true}
        ref={fileInputRef}
      />

      <AssignFilesToTypesDialog
        open={!!pendingAssign}
        files={pendingAssign?.files || []}
        types={pendingAssign?.types || []}
        isConfirming={isAssignUploading}
        onConfirm={handleAssignConfirm}
        onCancel={handleAssignCancel}
      />

      <GoMakeDeleteModal
        modalType={ModalType.WARNING}
        title={t("boardMissions.printJobModaTitle")}
        subTitle={" "}
        yesBtn={t("sales.quote.print")}
        openModal={isOpenPrintingModal.isOpen}
        onClose={isOpenPrintingModal.close}
        onClickDelete={() => { printJobUsingPrintNode(boardMissionId, productType).then() }}
      />
    </Stack>
  );
};

export { FilesGroupComponent };